import { faDog } from '@fortawesome/pro-light-svg-icons';
import { faHeartPulse, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { ListCard, ListCardRow } from 'components/UI/V3/ListCard';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import styles from './BelongingsTableRow.module.scss';

interface BelongingsItem {
    id: number;
    petName: string;
    petBreed: string;
    petPhoto?: string | null;
    hasMedicalAlert: boolean;
    belongings: string;
    date: string;
    roomName: string;
    unitName: string;
    locationName: string;
    notes: string;
}

interface BelongingsTableRowProps {
    item: BelongingsItem;
    className?: string;
}

const PetAvatar: FunctionComponent<{ photo?: string | null }> = ({ photo }) => (
    <div className={styles.petAvatar}>
        <div className={styles.petAvatarCircle}>
            {photo ? (
                <img
                    src={photo}
                    alt=""
                    className={styles.petAvatarPhoto}
                />
            ) : (
                <FontAwesomeIcon
                    icon={faDog}
                    className={styles.petSpeciesPlaceholder}
                />
            )}
        </div>
        {photo && (
            <span className={styles.petSpeciesOverlay}>
                <FontAwesomeIcon icon={faDog} />
            </span>
        )}
    </div>
);

export const BelongingsTableRow: FunctionComponent<BelongingsTableRowProps> = ({ item, className }) => {
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    const petContent = (
        <div className={styles.petRow}>
            <PetAvatar photo={item.petPhoto} />
            <div className={styles.petInfo}>
                <div className={styles.petNameRow}>
                    <span className={styles.petName}>{item.petName}</span>
                    {item.hasMedicalAlert && (
                        <FontAwesomeIcon
                            icon={faHeartPulse}
                            className={styles.healthIcon}
                        />
                    )}
                    {item.hasMedicalAlert && (
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className={styles.alertIcon}
                        />
                    )}
                </div>
                <span className={styles.petBreed}>{item.petBreed}</span>
            </div>
        </div>
    );

    const roomContent = (
        <div className={styles.roomStack}>
            <span className={styles.roomName}>
                {item.roomName} · {item.unitName}
            </span>
            <span className={styles.roomLocation}>
                <FontAwesomeIcon
                    icon={faLocationDot}
                    className={styles.roomLocationIcon}
                />
                {item.locationName}
            </span>
        </div>
    );

    if (isMobile) {
        return (
            <TableRow className={clsx(styles.mobileRow, className)}>
                <TableCell className={styles.mobileCell}>
                    <ListCard>
                        <ListCardRow>
                            <span className={styles.mobileRoomHeader}>
                                {item.roomName} · {item.unitName}
                            </span>
                            <span className={styles.mobileLocation}>
                                <FontAwesomeIcon
                                    icon={faLocationDot}
                                    className={styles.mobileLocationIcon}
                                />
                                {item.locationName}
                            </span>
                        </ListCardRow>

                        <ListCardRow>
                            <div className={styles.mobilePetSide}>{petContent}</div>
                            <span className={styles.mobileDateSide}>{item.date}</span>
                        </ListCardRow>

                        <ListCardRow className={styles.mobileSectionRow}>
                            <span className={styles.mobileSectionLabel}>Belongings</span>
                            <span className={styles.mobileSectionValue}>{item.belongings}</span>
                        </ListCardRow>

                        <ListCardRow className={styles.mobileSectionRow}>
                            <span className={styles.mobileSectionLabel}>Notes</span>
                            <span className={styles.mobileSectionValue}>
                                {item.notes || <span className={styles.empty}>–</span>}
                            </span>
                        </ListCardRow>
                    </ListCard>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <TableRow className={clsx(styles.row, className)}>
            <TableCell className={styles.petCell}>{petContent}</TableCell>
            <TableCell className={styles.belongingsCell}>{item.belongings}</TableCell>
            <TableCell className={styles.dateCell}>
                <span className={styles.date}>{item.date}</span>
            </TableCell>
            <TableCell className={styles.roomCell}>{roomContent}</TableCell>
            <TableCell className={styles.notesCell}>{item.notes || <span className={styles.empty}>–</span>}</TableCell>
        </TableRow>
    );
};

export default BelongingsTableRow;
